import { motion } from "framer-motion";
import { 
  Monitor, 
  FlaskConical, 
  BookOpenCheck, 
  Dumbbell, 
  Bus, 
  UtensilsCrossed,
  ShieldCheck,
  Laptop,
  Music
} from "lucide-react";

const facilities = [
  {
    title: "Smart Classrooms",
    icon: Monitor,
    image: "https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=800&q=80",
    description: "Our state-of-the-art smart classrooms are equipped with interactive digital boards, projectors, and audio-visual systems. These modern tools enhance the learning experience, making concepts easier to understand and remember.",
    features: [
      "Interactive digital boards",
      "High-definition projectors",
      "Audio-visual systems",
      "Climate-controlled environment",
      "Ergonomic furniture",
    ],
  },
  {
    title: "Computer Lab",
    icon: Laptop,
    image: "https://images.unsplash.com/photo-1606761568499-6d2451b23c66?auto=format&fit=crop&w=800&q=80",
    description: "Our computer laboratory is equipped with the latest hardware and software to provide students with hands-on experience in information technology. From basic computer skills to advanced programming, students learn essential digital skills.",
    features: [
      "Latest computers with high-speed internet",
      "Licensed educational software",
      "Coding and programming tools",
      "Dedicated IT instructors",
      "One computer per student",
    ],
  },
  {
    title: "Science Laboratories",
    icon: FlaskConical,
    image: "https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=800&q=80",
    description: "Well-equipped Physics, Chemistry, and Biology laboratories allow students to conduct experiments and understand scientific concepts through practical application. Safety measures and modern equipment ensure a secure learning environment.",
    features: [
      "Separate Physics, Chemistry, and Biology labs",
      "Modern scientific instruments",
      "Safety equipment and protocols",
      "Experienced lab assistants",
      "Regular practical sessions",
    ],
  },
  {
    title: "Library",
    icon: BookOpenCheck,
    image: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=800&q=80",
    description: "Our library houses an extensive collection of books, periodicals, journals, and digital resources. It's a quiet space for reading, research, and self-study, nurturing a love for reading and independent learning.",
    features: [
      "10,000+ books across all genres",
      "Digital library access",
      "Newspapers and magazines",
      "Reference and research materials",
      "Comfortable reading areas",
    ],
  },
  {
    title: "Sports Ground",
    icon: Dumbbell,
    image: "https://images.unsplash.com/photo-1461896836934-eba09b295a37?auto=format&fit=crop&w=800&q=80",
    description: "A sprawling sports ground provides facilities for various outdoor sports including cricket, football, basketball, and athletics. Physical education is an integral part of our curriculum, promoting fitness and teamwork.",
    features: [
      "Multi-purpose sports ground",
      "Cricket and football fields",
      "Basketball and volleyball courts",
      "Athletic track",
      "Indoor sports hall",
    ],
  },
  {
    title: "Transport Facility",
    icon: Bus,
    image: "https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=800&q=80",
    description: "Safe and reliable transport service with GPS-enabled buses covering all major routes. Our fleet is maintained regularly, and trained drivers ensure the safety of students during their commute.",
    features: [
      "GPS-enabled buses",
      "Trained and verified drivers",
      "Female attendant on each bus",
      "Multiple route coverage",
      "Real-time tracking for parents",
    ],
  },
  {
    title: "Cafeteria",
    icon: UtensilsCrossed,
    image: "https://images.unsplash.com/photo-1567521464027-f127ff144326?auto=format&fit=crop&w=800&q=80",
    description: "Our hygienic cafeteria serves nutritious and balanced meals prepared in a clean kitchen. We focus on providing healthy food options that cater to the dietary needs of growing children.",
    features: [
      "Hygienic food preparation",
      "Balanced and nutritious meals",
      "Variety of healthy options",
      "Clean and spacious dining area",
      "Qualified kitchen staff",
    ],
  },
  {
    title: "Security & Safety",
    icon: ShieldCheck,
    image: "https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=800&q=80",
    description: "Student safety is our top priority. Our campus is equipped with CCTV surveillance, trained security personnel, and strict visitor protocols to ensure a safe learning environment for all students.",
    features: [
      "24/7 CCTV surveillance",
      "Trained security personnel",
      "Visitor management system",
      "Fire safety equipment",
      "First aid facilities",
    ],
  },
  {
    title: "Music & Arts Room",
    icon: Music,
    image: "https://images.unsplash.com/photo-1511379938547-c1f69419868d?auto=format&fit=crop&w=800&q=80",
    description: "Dedicated spaces for music and arts allow students to explore their creative talents. From learning musical instruments to painting and sculpture, we nurture artistic expression in every child.",
    features: [
      "Musical instruments available",
      "Art supplies and materials",
      "Trained music and art teachers",
      "Regular performances and exhibitions",
      "Soundproof practice rooms",
    ],
  },
];

const Facilities = () => {
  return (
    <div data-testid="facilities-page">
      {/* Page Header */}
      <section className="bg-primary py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              Our Facilities
            </h1>
            <p className="text-white/80 text-lg max-w-2xl mx-auto">
              World-class infrastructure designed to provide the best learning environment
            </p>
          </motion.div>
        </div>
      </section>

      {/* Facilities Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {facilities.map((facility, index) => (
              <motion.div
                key={facility.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? '' : ''
                }`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <facility.icon className="text-primary" size={24} />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">
                      {facility.title}
                    </h2>
                  </div>
                  
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {facility.description}
                  </p>

                  <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {facility.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-gray-600">
                        <div className="w-2 h-2 bg-secondary rounded-full flex-shrink-0"></div>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative rounded-2xl overflow-hidden shadow-xl group">
                    <img
                      src={facility.image}
                      alt={facility.title}
                      className="w-full h-[350px] object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl font-bold text-gray-900 mb-4">
              Want to See Our Campus?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Schedule a campus visit to experience our world-class facilities firsthand. 
              We'd love to show you around!
            </p>
            <a 
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
            >
              Schedule a Visit
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Facilities;
